import { type CSSProperties, useEffect, useId, useState } from 'react';

const zoomModes = [
  { value: 'fit', label: 'Fit' },
  { value: 'actual', label: '1x' },
  { value: 'wide', label: '1.5x' },
  { value: 'deep', label: '2x' },
] as const;

type ZoomMode = (typeof zoomModes)[number]['value'];

interface ZoomableImageProps {
  src: string;
  alt: string;
  label: string;
  className?: string;
  imageClassName?: string;
  loading?: 'eager' | 'lazy';
  width?: number;
  height?: number;
}

export function ZoomableImage({
  src,
  alt,
  label,
  className = '',
  imageClassName = '',
  loading = 'lazy',
  width,
  height,
}: ZoomableImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [zoomMode, setZoomMode] = useState<ZoomMode>('fit');
  const titleId = useId();

  const openViewer = () => {
    setZoomMode('fit');
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        aria-label={`Open ${label}`}
        className={`group block w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-4 focus-visible:ring-offset-paper ${className}`}
        onClick={openViewer}
      >
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={`h-auto w-full transition duration-300 group-hover:scale-[1.01] ${imageClassName}`}
          loading={loading}
          decoding="async"
        />
      </button>

      {isOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-50 bg-ink/90 px-4 py-5 text-paper md:px-8 md:py-7"
          onClick={() => setIsOpen(false)}
        >
          <div className="mx-auto flex h-full max-w-7xl flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 id={titleId} className="font-display text-lg tracking-tight md:text-2xl">
                {label}
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <div className="flex overflow-hidden rounded-sm border border-paper/20">
                  {zoomModes.map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      aria-pressed={zoomMode === mode.value}
                      className={`px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-paper ${
                        zoomMode === mode.value
                          ? 'bg-paper text-ink'
                          : 'text-paper/75 hover:bg-paper/10 hover:text-paper'
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setZoomMode(mode.value);
                      }}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                <a
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-sm border border-paper/20 px-4 py-2 text-sm font-medium text-paper/80 transition hover:border-paper/50 hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-paper"
                  onClick={(event) => event.stopPropagation()}
                >
                  Full image
                </a>
                <button
                  type="button"
                  aria-label="Close image viewer"
                  className="flex h-10 w-10 items-center justify-center rounded-sm border border-paper/20 text-lg font-semibold text-paper/80 transition hover:border-paper/50 hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-paper"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen(false);
                  }}
                >
                  X
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto" onClick={(event) => event.stopPropagation()}>
              <img
                src={src}
                alt={alt}
                width={width}
                height={height}
                className={`mx-auto h-auto max-h-none max-w-none rounded-sm bg-paper ${
                  zoomMode === 'fit' ? 'w-full md:w-auto md:max-w-full' : ''
                }`}
                style={getZoomStyle(zoomMode, width)}
                decoding="async"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function getZoomStyle(zoomMode: ZoomMode, sourceWidth?: number): CSSProperties | undefined {
  if (zoomMode === 'fit') {
    return undefined;
  }

  const scaleByMode: Record<Exclude<ZoomMode, 'fit'>, number> = {
    actual: 1,
    wide: 1.5,
    deep: 2,
  };

  const scale = scaleByMode[zoomMode];

  if (sourceWidth) {
    return { width: `${sourceWidth * scale}px` };
  }

  return { width: `${scale * 100}%` };
}
